<template>
  <v-card class="mx-auto" shaped width="100%">
    <v-card-title
      class="headline mb-1 orange darken-3 white--text"
    ></v-card-title>
    <v-col cols="12" sm="6" md="6" lg="4">
      <v-text-field
        class="mt-3"
        :label="translations.companyNameLabel[locale]"
        v-model="companyName"
      ></v-text-field>
      <v-text-field
        :label="translations.answererLabel[locale]"
        v-model="answerer"
      ></v-text-field>
      <v-menu
        ref="dateMenu"
        v-model="dateMenu"
        :close-on-content-click="false"
        transition="scale-transition"
        offset-y
        max-width="290px"
        min-width="290px"
      >
        <template v-slot:activator="{ on }">
          <v-text-field
            v-model="dateFormatted"
            :label="translations.dateLabel[locale]"
            @blur="date = parseDate(dateFormatted)"
            v-on="on"
          ></v-text-field>
        </template>
        <v-date-picker
          v-model="date"
          no-title
          @input="dateMenu = false"
        ></v-date-picker>
      </v-menu>
    </v-col>
    <hr class="mb-3" />
    <div class="pa-3 title">
      {{ translations.handicapResultHeader[locale] }}
      <div class="headline">{{ handicapAverage }}</div>
    </div>
    <div class="pa-3 pb-0 subtitle-1">
      {{ translations.handicapsHeader[locale] }}
    </div>
    <v-layout
      class="pa-3 pb-0"
      v-for="(s, i) in summary"
      :key="`summary_${i + 1}`"
    >
      <div>{{ s[locale] }}</div>
    </v-layout>
    <div class="charts-wrapper float-left mt-10">
      <div class="chart-size float-left">
        <HorizontalBarChart
          :chart-data="horizontalBarDataCollection"
          :options="chartOptions"
        ></HorizontalBarChart>
      </div>
      <div class="chart-size float-left">
        <RadarChart
          :chart-data="radarDataCollection"
          :options="chartOptions"
        ></RadarChart>
      </div>
    </div>
    <v-btn class="ma-3" color="primary" @click="createPDF">
      <v-icon left>mdi-file-pdf-outline</v-icon>
      PDF
    </v-btn>
  </v-card>
</template>

<script>
import RadarChart from "../charts/radar.chart";
import HorizontalBarChart from "../charts/horizontal-bar.chart";
import jsPDF from "jspdf";
import { RESULT_PAGE_TRANSLATIONS } from "../constants/translations";
import digicapQuestions from "../questions/digicap.questions";
import ChartJSPluginDatalabels from "chartjs-plugin-datalabels";

export default {
  name: "Result",
  props: {
    categories: Array,
    inputsAll: Object,
    questionsWeights: Object
  },
  components: {
    HorizontalBarChart,
    RadarChart
  },
  data() {
    return {
      horizontalBarDataCollection: {},
      radarDataCollection: {},
      chartOptions: {},
      date: new Date().toISOString().substr(0, 10),
      dateFormatted: this.formatDate(new Date().toISOString().substr(0, 10)),
      dateMenu: false,
      companyName: "",
      answerer: "",
      translations: RESULT_PAGE_TRANSLATIONS,
      summary: digicapQuestions.summary,
      handicapAverage: 0
    };
  },
  mounted() {
    this.createData();
  },
  watch: {
    date(val) {
      this.dateFormatted = this.formatDate(this.date);
    }
  },
  methods: {
    createData: function() {
      const labels = [];
      const averages = [];
      const handicaps = [];
      const handicapMultipliers = [1, 0.75, 0.5, 0.25, 0];
      let index = 1;

      for (const category of this.categories) {
        if (index < this.categories.length) {
          labels.push(category.name[this.locale]);

          const average = category.questions.reduce((accumulator, id) => {
            if (this.inputsAll[id] !== undefined) {
              return (
                accumulator + this.inputsAll[id] * this.questionsWeights[id]
              );
            }

            return accumulator;
          }, 0);

          const handicap = category.questions.reduce((accumulator, id) => {
            if (this.inputsAll[id] !== undefined) {
              return (
                accumulator +
                (54 / category.questions.length) *
                  handicapMultipliers[this.inputsAll[id] - 1]
              );
            }

            return accumulator;
          }, 0);

          averages.push(average.toFixed(1));
          handicaps.push(handicap.toFixed(1));
        }

        index++;
      }

      this.handicapAverage = (
        handicaps.reduce((accumulator, current) => {
          return accumulator + parseFloat(current);
        }, 0) / handicaps.length
      ).toFixed(1);

      const radarDatasets = [
        {
          label: this.translations.radarLabel[this.locale],
          borderColor: "orange",
          lineTension: 0.2,
          borderWidth: 2.5,
          data: averages
        }
      ];

      const horizontalBarDatasets = [
        {
          label: this.translations.horizontalBarLabel[this.locale],
          borderColor: "orange",
          lineTension: 0.2,
          borderWidth: 2.5,
          data: handicaps
        }
      ];

      this.fillChart(labels, radarDatasets, horizontalBarDatasets);
    },
    fillChart: function(labels, radarDatasets, horizontalBarDatasets) {
      this.radarDataCollection = {
        labels,
        datasets: radarDatasets
      };

      this.horizontalBarDataCollection = {
        labels,
        datasets: horizontalBarDatasets
      };
    },
    createPDF: function() {
      const createPdfBackground = doc => {
        // Asr-logo
        const asrLogo = new Image();
        const asrImageSrc = require("../assets/asr-logo-03.png");
        asrLogo.src = asrImageSrc;
        doc.addImage(asrLogo, "PNG", 152, 0, 60, 15);

        // Digicap-logo
        const digicapLogo = new Image();
        const digicapImageSrc = require("../assets/digicap-logo-01.png");
        digicapLogo.src = digicapImageSrc;
        doc.addImage(digicapLogo, "PNG", 90, 1, 60, 15);

        // Color background bars
        doc.setFillColor(243, 148, 32);

        // Top right bar
        doc.rect(0, 0, 60, 10, "F");
        doc.triangle(60, 0, 60, 10, 70, 0, "F");

        // Top left bar
        doc.rect(0, 10, 10, 60, "F");
        doc.triangle(0, 70, 10, 70, 0, 80, "F");

        // Bottom right bar
        doc.rect(200, 210, 10, 90, "F");
        doc.triangle(200, 210, 210, 210, 210, 200, "F");

        // Bottom left bar
        doc.rect(120, 287, 80, 10, "F");
        doc.triangle(110, 297, 120, 297, 120, 287, "F");

        // Color header box
        doc.setFillColor(0, 0, 0);

        // Header box
        doc.rect(0, 0, 40, 23, "F");

        // Header text
        doc.setFontStyle("bold");
        doc.setTextColor(255);

        doc.text("LAPLAND", 6, 10);

        doc.setFontSize(10);
        doc.text("Above Ordinary", 6, 16);

        return doc;
      };

      let doc = new jsPDF("portait");

      // Background
      doc = createPdfBackground(doc);

      // CONTENT

      // Charts
      const radarCanvas = document.getElementById("radar-chart");
      const radarCanvasImg = radarCanvas.toDataURL("image/png", 1.0);
      const horizontalBarCanvas = document.getElementById(
        "horizontalbar-chart"
      );
      const horizontalBarCanvasImg = horizontalBarCanvas.toDataURL(
        "image/png",
        1.0
      );

      // Title
      doc.setTextColor(0);
      doc.setFontSize(20);
      doc.text(
        20,
        37,
        this.categories[this.categories.length - 1].name[this.locale]
      );

      doc.setFontSize(12);
      doc.setFontStyle("normal");

      // Form info
      doc.text(
        20,
        50,
        `${this.translations.companyNameLabel[this.locale]}: ${
          this.companyName
        }`
      );
      doc.text(
        20,
        55,
        `${this.translations.answererLabel[this.locale]}: ${this.answerer}`
      );
      doc.text(
        20,
        60,
        `${this.translations.dateLabel[this.locale]}: ${this.dateFormatted}`
      );

      // Result
      doc.setFontSize(16);
      doc.text(20, 80, this.translations.handicapResultHeader[this.locale]);

      doc.setFontSize(16);
      doc.text(20, 90, this.handicapAverage);

      doc.setFontSize(12);
      doc.text(20, 100, this.translations.handicapsHeader[this.locale]);

      const text = this.summary.reduce((accumulator, item) => {
        return (accumulator += `${item[this.locale]}\r\n`);
      }, "");

      doc.setFontSize(10);
      doc.text(20, 105, text);

      // Add Charts
      doc.addImage(horizontalBarCanvasImg, "PNG", 20, 130, 165, 150);
      doc.addPage();
      doc = createPdfBackground(doc);
      doc.addImage(radarCanvasImg, "PNG", 25, 37, 165, 165);

      doc.save(
        `${this.categories[this.categories.length - 1].name[this.locale]}.pdf`
      );
    },
    formatDate(date) {
      if (!date) return null;
      const [year, month, day] = date.split("-");
      return `${month}/${day}/${year}`;
    },
    parseDate(date) {
      if (!date) return null;
      const [month, day, year] = date.split("/");
      return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
    }
  },
  computed: {
    computedDateFormatted() {
      return this.formatDate(this.date);
    },
    locale() {
      return this.$store.getters.getLocale;
    }
  }
};
</script>

<style scoped>
.charts-wrapper {
  width: 100%;
}
.chart-size {
  margin: 15px;
  min-width: 30vw;
}
</style>
